import React, { useEffect, useState } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { closeModal } from 'reducers/modals';
// components
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import Modal from 'components/Modal';
import Button from 'components/Button';
import Input from 'components/Input';
import ReactSelect from 'components/Select/ReactSelect';
import SingleDatePicker from 'components/DatePicker/SingleDatePicker/SingleDatePicker';
// endpoints
import { getFeedDeliveriesFarmGroups, getFeedDeliveriesBins, getFeedDeliveriesRations } from 'endpoints/feedDeliveries';
// utils
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { lbsToDisplay, displayToLbs, weightUnit } from '../utils';

const DeliveryFormModal = ({ farmId, delivery, measurementSystem, onSubmit, closeModal }) => {
  const isEditing = Boolean(delivery);
  const [groups, setGroups] = useState([]);
  const [bins, setBins] = useState([]);
  const [rations, setRations] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    pig_group_id: delivery?.pig_group_id || null,
    bin_id: delivery?.bin_id || null,
    feed_ration_id: delivery?.feed_ration_id || null,
    delivery_date: delivery ? moment(delivery.delivery_date) : moment(),
    quantity: delivery ? lbsToDisplay(delivery.quantity, measurementSystem) : '',
  });

  useEffect(() => {
    getFeedDeliveriesFarmGroups(farmId, { per_page: 100 })
      .then((response) => setGroups(response.resources || []))
      .catch(toastResponseErrors);
    getFeedDeliveriesBins(farmId, { per_page: 100 }).then(setBins).catch(toastResponseErrors);
    getFeedDeliveriesRations().then(setRations).catch(toastResponseErrors);
  }, [farmId]);

  const setField = (field) => (value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const selectedRation = rations.find(({ id }) => id === formData.feed_ration_id);
  // Medication is read-only, auto-populated from Ration Setup
  const medications = selectedRation?.medications || delivery?.medications || [];

  const isValid = formData.pig_group_id && formData.bin_id && formData.feed_ration_id
    && formData.delivery_date && Number(formData.quantity) > 0;

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      await onSubmit({
        pig_group_id: formData.pig_group_id,
        bin_id: formData.bin_id,
        feed_ration_id: formData.feed_ration_id,
        delivery_date: formData.delivery_date.format('YYYY-MM-DD'),
        quantity: displayToLbs(formData.quantity, measurementSystem),
      });
      closeModal();
    } catch (error) {
      toastResponseErrors(error);
    } finally {
      setIsSaving(false);
    }
  };

  const title = isEditing
    ? <FormattedMessage id="component.feedDeliveries.editDelivery" />
    : <FormattedMessage id="component.feedDeliveries.addDelivery" />;

  return (
    <Modal title={title} className="DeliveryFormModal">
      <section className="modal-body">
        <div className="form-row">
          <span className="input-label"><FormattedMessage id="general.group" /></span>
          <ReactSelect
            placeholder={<FormattedMessage id="general.selectGroup" />}
            options={groups}
            valueKey="id"
            labelKey="name"
            value={formData.pig_group_id}
            onChange={(option) => setField('pig_group_id')(option ? option.id : null)}
            noInputMargin
          />
        </div>

        <div className="form-row">
          <span className="input-label"><FormattedMessage id="general.deliveryDate" /></span>
          <SingleDatePicker
            inputId="feedDeliveryDateInput"
            className="date-input"
            date={formData.delivery_date}
            onDateChange={setField('delivery_date')}
            isOutsideRange={(day) => day.isAfter(moment(), 'day')}
          />
        </div>

        <div className="form-row">
          <span className="input-label"><FormattedMessage id="component.dataTable.headers.binID" /></span>
          <ReactSelect
            placeholder={<FormattedMessage id="general.selectBin" />}
            options={bins}
            valueKey="id"
            labelKey="name"
            value={formData.bin_id}
            onChange={(option) => setField('bin_id')(option ? option.id : null)}
            noInputMargin
            searchable={false}
          />
        </div>

        <div className="form-row">
          <span className="input-label"><FormattedMessage id="general.ration" /></span>
          <ReactSelect
            placeholder={<FormattedMessage id="component.feedDeliveries.selectRation" />}
            options={rations}
            valueKey="id"
            labelKey="name"
            value={formData.feed_ration_id}
            onChange={(option) => setField('feed_ration_id')(option ? option.id : null)}
            noInputMargin
          />
        </div>

        <div className="form-row">
          <span className="input-label"><FormattedMessage id="general.medications" /></span>
          <div className="medications-value">
            {medications.length
              ? medications.map(({ name }) => name).join(', ')
              : <FormattedMessage id="general.notAvail" />}
          </div>
        </div>

        <div className="form-row">
          <span className="input-label">
            <FormattedMessage id="component.feedDeliveries.qty" /> ({weightUnit(measurementSystem)})
          </span>
          <Input
            type="number"
            value={formData.quantity}
            onChange={(e) => setField('quantity')(e.target.value)}
            min={0}
            step={0.01}
            required
          />
        </div>
      </section>

      <section className="modal-footer text-right">
        <Button className="margin-right-1" onClick={closeModal} disabled={isSaving}>
          <FormattedMessage id="general.button.cancel" />
        </Button>
        <Button primary onClick={handleSubmit} disabled={isSaving || !isValid}>
          {isEditing
            ? <FormattedMessage id="general.button.update" />
            : <FormattedMessage id="general.button.add" />}
        </Button>
      </section>
    </Modal>
  );
};

DeliveryFormModal.propTypes = {
  farmId: T.number.isRequired,
  delivery: T.object,
  measurementSystem: T.oneOf(['imperial', 'metric']),
  onSubmit: T.func.isRequired,
  closeModal: T.func.isRequired,
};

export default connect(null, { closeModal })(DeliveryFormModal);
